import React, { useCallback, useState } from "react";
import { FileText, FolderOpen, Save, X, HelpCircle } from "lucide-react";
import PdfViewer from "../viewer/PdfViewer";
import EmptyState from "./EmptyState";
import StatusBar from "./StatusBar";
import AppLayout from "./AppLayout";
import PasswordPrompt from "./PasswordPrompt";
import HelpAboutModal from "./HelpAboutModal";
import TabBar from "./TabBar";
import ToolSidebar, { ToolKey } from "../tools/ToolSidebar";
import ToolPanel from "../tools/ToolPanel";
import { DocumentProvider, useDocument } from "../state/documentStore";
import { savePdf } from "../services/fileService";
import { useBackendStatus } from "../services/backendClient";
import "./desktop.css";

/** Context consumer: composes the document-first frame. */
const DesktopShell: React.FC = () => {
  const {
    open,
    tabs,
    activeId,
    currentPage,
    dirty,
    error,
    recent,
    passwordPrompt,
    pickFile,
    openFileObject,
    openPath,
    closeDocument,
    closeTab,
    switchTab,
    setCurrentPage,
    clearError,
    submitPassword,
    cancelPasswordPrompt,
    signature,
    setSignature,
  } = useDocument();

  const [activeTool, setActiveTool] = useState<ToolKey | null>(null);

  const placeSignature = useCallback(
    (pageNumber: number, xFrac: number, yFrac: number) =>
      setSignature({ pageNumber, xFrac, yFrac, armed: false }),
    [setSignature],
  );
  const [showHelp, setShowHelp] = useState(false);
  const backendState = useBackendStatus();

  const save = useCallback(() => {
    if (open) savePdf(open.fileName, open.bytes);
  }, [open]);

  const onWindowDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const file = Array.from(e.dataTransfer.files).find((f) =>
        f.name.toLowerCase().endsWith(".pdf"),
      );
      if (file) openFileObject(file);
    },
    [openFileObject],
  );

  const titleBar = (
    <>
      <div className="desktop-brand">
        <img
          src={`${process.env.PUBLIC_URL}/assets/logo.png`}
          alt=""
          className="desktop-logo"
        />
        <span className="desktop-appname">HyperPDF</span>
      </div>
      {open && (
        <div className="desktop-doc-title" title={open.fileName}>
          <FileText size={14} />
          <span>{open.fileName}</span>
        </div>
      )}
      <div className="desktop-titlebar-actions">
        <button className="desktop-tb-btn" onClick={pickFile} title="Open PDF">
          <FolderOpen size={16} />
          Open
        </button>
        {open && (
          <button
            className={`desktop-tb-btn ${dirty ? "desktop-tb-btn-accent" : ""}`}
            onClick={save}
            title="Save a copy"
          >
            <Save size={16} />
            {dirty ? "Save*" : "Save"}
          </button>
        )}
        {open && (
          <button
            className="desktop-tb-btn"
            onClick={() => {
              closeDocument();
              setActiveTool(null);
            }}
            title="Close document"
          >
            <X size={16} />
          </button>
        )}
        <button
          className="desktop-tb-btn"
          onClick={() => setShowHelp(true)}
          title="Help & about"
        >
          <HelpCircle size={16} />
        </button>
      </div>
    </>
  );

  const banner = error ? (
    <div className="desktop-error">
      {error}
      <button onClick={clearError}>
        <X size={14} />
      </button>
    </div>
  ) : undefined;

  const main = open ? (
    <div className="content-row">
      <PdfViewer
        key={open.fileName + open.bytes.byteLength}
        doc={open.doc}
        fileName={open.fileName}
        onCurrentPageChange={setCurrentPage}
        signature={activeTool === "sign" ? signature : null}
        onSignaturePlace={placeSignature}
      />
      {activeTool && (
        <ToolPanel tool={activeTool} onClose={() => setActiveTool(null)} />
      )}
    </div>
  ) : (
    <EmptyState
      recent={recent}
      onPickFile={pickFile}
      onOpenFileObject={openFileObject}
      onOpenRecent={openPath}
    />
  );

  const sidebar = open ? (
    <ToolSidebar
      activeTool={activeTool}
      onSelect={(t) => setActiveTool((cur) => (cur === t ? null : t))}
    />
  ) : undefined;

  const statusBar = (
    <StatusBar
      fileName={open?.fileName}
      currentPage={currentPage}
      totalPages={open?.doc.numPages}
      sizeBytes={open?.bytes.byteLength}
      backend={backendState}
    />
  );

  return (
    <div
      className="desktop-app"
      onDragOver={(e) => e.preventDefault()}
      onDrop={onWindowDrop}
    >
      <AppLayout
        titleBar={titleBar}
        tabBar={
          <TabBar
            tabs={tabs}
            activeId={activeId}
            onSelect={switchTab}
            onClose={closeTab}
            onOpen={pickFile}
          />
        }
        banner={banner}
        main={main}
        sidebar={sidebar}
        statusBar={statusBar}
      />
      {passwordPrompt && (
        <PasswordPrompt
          fileName={passwordPrompt.fileName}
          incorrect={passwordPrompt.incorrect}
          onSubmit={submitPassword}
          onCancel={cancelPasswordPrompt}
        />
      )}
      {showHelp && <HelpAboutModal onClose={() => setShowHelp(false)} />}
    </div>
  );
};

/**
 * Document-centric desktop shell. Provides the document store and renders the
 * document-first frame. Replaces the web routes when IS_DESKTOP (see App.tsx).
 */
const DesktopApp: React.FC = () => (
  <DocumentProvider>
    <DesktopShell />
  </DocumentProvider>
);

export default DesktopApp;
